/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { ERROR_CHART_MESSAGE, NO_DATA_TITLE_BY_METRIC_TYPE } from '@modules/autoops-lib/constants'
import type { MetricItem, ChartType } from '@modules/autoops-api/types'

import type { MessageDescriptor } from 'react-intl'

// We're knowingly dividing by 1024^3 (i.e. GiB) because that's how usage is billed. However, this amount will be shown
// using the GB unit like everywhere else (Billing page, Kibana), even though it is not the right unit.
// This is mainly for consistency's sake.
export const bytesToGigabytes = (bytes: number): number => {
  const NUMBER_OF_BYTES_IN_A_GB = 1024 * 1024 * 1024

  return bytes / NUMBER_OF_BYTES_IN_A_GB
}

export const truncateToFirstNonZero = (value: number) => {
  if (value === 0) {
    return 0
  }

  if (Math.abs(value) < 1) {
    // Round to the first significant digit
    const exponent = Math.floor(Math.log10(Math.abs(value)))
    const decimalPlaces = -exponent + 1
    return parseFloat(value.toFixed(decimalPlaces))
  }

  // Round to 2 decimal places
  return parseFloat(value.toFixed(2))
}

export const generateCallout = (
  chartType: ChartType,
  items: Array<MetricItem | undefined>,
): { type: 'info' | 'danger'; title: MessageDescriptor; message?: string } => {
  const definedItems = items.filter((item) => item !== undefined)
  const errorItems = definedItems.filter((item) => item.error !== null)

  if (items.length === errorItems.length) {
    return { type: 'danger', title: ERROR_CHART_MESSAGE, message: items[0]?.error ?? '' }
  }

  return { type: 'info', title: NO_DATA_TITLE_BY_METRIC_TYPE[chartType] }
}

const isMoreThanOneHour = (from: number, to: number) => {
  const diffMs = Math.abs(to - from)
  const oneHourMs = 60 * 60 * 1000

  return diffMs > oneHourMs
}

const dailyFormat = 'MMM DD'
const hourlyFormat = 'MMM DD HH:mm'

export const getFormatFromMetricItem = (data: Array<[number, number]>) => {
  const first = data[0]
  const second = data[1]

  if (!first || !second) {
    return dailyFormat
  }

  const isMore = isMoreThanOneHour(first[0], second[0])

  return isMore ? dailyFormat : hourlyFormat
}

export const calculateDomain = (item: MetricItem | undefined, index: 0 | 1 = 1) => {
  if (!item?.data) {
    return { min: 0, max: 0 }
  }

  const { data } = item

  if (data.length === 0) {
    return { min: 0, max: 0 }
  }

  const values = data.map((unit) => unit[index])

  return { min: Math.min(...values), max: Math.max(...values) }
}

export const generateAutoOpsUrl = ({
  region,
  organizationId,
  deploymentId,
}: {
  region: string
  organizationId: string
  deploymentId: string
}) =>
  `https://app.auto-ops.${window.location.hostname}/regions/${region}/organizations/${organizationId}/deployments/${deploymentId}/deployment`

export const generateProjectAutoOpsUrl = ({
  region,
  organizationId,
  resourceId,
}: {
  region: string
  organizationId: string
  resourceId: string
}) =>
  `https://application.auto-ops.${window.location.hostname}/regions/${region}/organizations/${organizationId}/projects/${resourceId}/search-ai-lake`

export const generateConnectedClusterAutoOpsUrl = ({
  region,
  organizationId,
  ccmResourceId,
}: {
  region: string
  organizationId: string
  ccmResourceId: string
}) =>
  `https://app.auto-ops.${window.location.hostname}/regions/${region}/organizations/${organizationId}/clusters/${ccmResourceId}/cluster`

export const generateConnectAutoOpsWizardUrl = ({ organizationId }: { organizationId: string }) =>
  `https://application.auto-ops.${window.location.hostname}/organizations/${organizationId}/connect-autoops`
